import React from 'react';
import { Box, Container, Grid, Typography, Link, IconButton, Divider, CircularProgress, Button } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import YouTubeIcon from '@mui/icons-material/YouTube';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useSettings } from '../contexts/SettingsContext';
import { useContact } from '../contexts/ContactContext';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const { settings, loading: settingsLoading } = useSettings();
  const { contactInfo, loading: contactLoading } = useContact();

  // Filter navigation links based on visibility settings
  const navLinks = [
    { title: 'Home', path: '/', key: 'home' },
    { title: 'About Us', path: '/about', key: 'about' },
    { title: 'Services', path: '/services', key: 'services' },
    { title: 'Portfolio', path: '/portfolio', key: 'portfolio' },
    { title: 'Tools', path: '/tools', key: 'tools' },
    { title: 'Equipment Rental', path: '/rental', key: 'rental' },
    { title: 'Contact', path: '/contact', key: 'contact' },
  ].filter(link => {
    // Home is always visible
    if (link.key === 'home') return true;
    
    // Check visibility for other pages
    return settings.pageVisibility[link.key as keyof typeof settings.pageVisibility];
  });

  if (settingsLoading || contactLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box 
      component="footer" 
      sx={{ 
        bgcolor: 'background.paper', 
        py: 6,
        borderTop: '1px solid',
        borderColor: 'divider',
        mt: 'auto'
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Company Info and Social Media */}
          <Grid item xs={12} md={5}>
            <Typography variant="h6" color="primary.main" gutterBottom>
              PARAGON LIVE DESIGN
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Specializing in production design, consulting, drafting, production management, and equipment rental services.
            </Typography>
            
            {/* Social Media Icons */}
            <Box sx={{ mt: 2, mb: 3 }}>
              {settings.social.facebook && settings.social.facebook.trim() && (
                <IconButton 
                  color="primary" 
                  aria-label="Facebook" 
                  component="a" 
                  href={settings.social.facebook} 
                  target="_blank"
                >
                  <FacebookIcon />
                </IconButton>
              )}
              {settings.social.instagram && settings.social.instagram.trim() && (
                <IconButton 
                  color="primary" 
                  aria-label="Instagram" 
                  component="a" 
                  href={settings.social.instagram} 
                  target="_blank"
                >
                  <InstagramIcon />
                </IconButton>
              )}
              {settings.social.linkedin && settings.social.linkedin.trim() && (
                <IconButton 
                  color="primary" 
                  aria-label="LinkedIn" 
                  component="a" 
                  href={settings.social.linkedin} 
                  target="_blank"
                >
                  <LinkedInIcon />
                </IconButton>
              )}
              {settings.social.youtube && settings.social.youtube.trim() && (
                <IconButton 
                  color="primary" 
                  aria-label="YouTube" 
                  component="a" 
                  href={settings.social.youtube} 
                  target="_blank"
                >
                  <YouTubeIcon />
                </IconButton>
              )}
            </Box>
            
            {/* Contact Button */}
            {settings.pageVisibility.contact && (
              <Button 
                variant="outlined" 
                color="primary" 
                component={RouterLink}
                to="/contact"
                startIcon={<EmailIcon />}
                sx={{ mt: 1 }}
              >
                Get in Touch
              </Button>
            )}
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="h6" color="primary.main" gutterBottom>
              Quick Links
            </Typography>
            <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
              {navLinks.map((link) => (
                <Box component="li" sx={{ mb: 1 }} key={link.key}>
                  <Link component={RouterLink} to={link.path} color="text.secondary" underline="hover">
                    {link.title}
                  </Link>
                </Box>
              ))}
            </Box>
          </Grid>

          {/* Contact Info - Simplified */}
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="h6" color="primary.main" gutterBottom>
              Contact Information
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {contactInfo.email && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailIcon color="action" fontSize="small" />
                  <Typography variant="body2" color="text.secondary">
                    <Link href={`mailto:${contactInfo.email}`} color="inherit" underline="hover">
                      {contactInfo.email}
                    </Link>
                  </Typography>
                </Box>
              )}
              
              {contactInfo.phone && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhoneIcon color="action" fontSize="small" />
                  <Typography variant="body2" color="text.secondary">
                    <Link href={`tel:${contactInfo.phone.replace(/[^0-9+]/g, '')}`} color="inherit" underline="hover">
                      {contactInfo.phone}
                    </Link>
                  </Typography>
                </Box>
              )}
              
              {contactInfo.address && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <LocationOnIcon color="action" fontSize="small" sx={{ mt: 0.3 }} />
                  <Typography variant="body2" color="text.secondary">
                    {contactInfo.address}
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        {/* Copyright */}
        <Typography variant="body2" color="text.secondary" align="center">
          © {currentYear} Paragon Live Design. All rights reserved.
        </Typography>
      </Container>
    </Box>
  );
};

export default Footer; 